package com.patronat.top.ui.organisms.venue

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import com.patronat.top.ui.models.VenueGalleryPhoto
import com.patronat.top.ui.theme.AccentPink
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.LabelLarge
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary100
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.VenueSectionTitleText
import com.patronat.top.ui.theme.White
import kotlin.math.max

@Composable
fun VenueGallerySection(
    photos: List<VenueGalleryPhoto>,
    onUploadClick: () -> Unit,
    onPhotoRemoveClick: (VenueGalleryPhoto) -> Unit,
    modifier: Modifier = Modifier,
    title: String = "Галерея",
    leadingIcon: Painter? = null,
    uploadTileText: String = "Завантажити фото",
    showUploadTile: Boolean = true,
    uploadTileEnabled: Boolean = true,
    highlightError: Boolean = false,
    errorPhotoIds: Set<String> = emptySet(),
    tileSize: Dp = 260.dp,
    tileSpacing: Dp = 8.dp
) {
    val borderColor = if (highlightError) AccentPink else MineShaft100
    Column(
        modifier = modifier
            .fillMaxWidth()
            .background(White, RoundedCornerShape(12.dp))
            .border(1.dp, borderColor, RoundedCornerShape(12.dp))
            .padding(16.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (leadingIcon != null) {
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .background(Primary100, RoundedCornerShape(8.dp)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = leadingIcon,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp)
                    )
                }
                Spacer(modifier = Modifier.size(8.dp))
            }

            Text(
                text = title,
                style = VenueSectionTitleText,
                color = MineShaft950
            )
        }

        Spacer(modifier = Modifier.height(18.dp))

        GalleryGrid(
            photos = photos,
            onUploadClick = onUploadClick,
            onPhotoRemoveClick = onPhotoRemoveClick,
            uploadTileText = uploadTileText,
            showUploadTile = showUploadTile,
            uploadTileEnabled = uploadTileEnabled,
            errorPhotoIds = errorPhotoIds,
            tileSize = tileSize,
            tileSpacing = tileSpacing
        )
    }
}

@Composable
private fun GalleryGrid(
    photos: List<VenueGalleryPhoto>,
    onUploadClick: () -> Unit,
    onPhotoRemoveClick: (VenueGalleryPhoto) -> Unit,
    uploadTileText: String,
    showUploadTile: Boolean,
    uploadTileEnabled: Boolean,
    errorPhotoIds: Set<String>,
    tileSize: Dp,
    tileSpacing: Dp
) {
    val items = buildList<GalleryGridItem> {
        if (showUploadTile) add(GalleryGridItem.Upload)
        photos.forEach { add(GalleryGridItem.Photo(it)) }
    }

    if (items.isEmpty()) return

    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val columns = max(
            1,
            ((maxWidth + tileSpacing) / (tileSize + tileSpacing)).toInt()
        )
        val rows = items.chunked(columns)

        Column(verticalArrangement = Arrangement.spacedBy(tileSpacing)) {
            rows.forEach { row ->
                Row(
                    horizontalArrangement = Arrangement.spacedBy(tileSpacing)
                ) {
                    row.forEach { item ->
                        when (item) {
                            GalleryGridItem.Upload -> {
                                UploadPhotoTile(
                                    text = uploadTileText,
                                    enabled = uploadTileEnabled,
                                    onClick = onUploadClick,
                                    modifier = Modifier.size(tileSize)
                                )
                            }

                            is GalleryGridItem.Photo -> {
                                VenuePhotoTile(
                                    photo = item.value,
                                    highlightError = item.value.id in errorPhotoIds,
                                    onRemoveClick = onPhotoRemoveClick,
                                    modifier = Modifier.size(tileSize)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun UploadPhotoTile(
    text: String,
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val shape = RoundedCornerShape(8.dp)

    Box(
        modifier = modifier
            .clip(shape)
            .background(MineShaft50)
            .drawBehind {
                val stroke = 2.dp.toPx()
                drawLine(
                    color = MineShaft50,
                    start = Offset(0f, stroke / 2),
                    end = Offset(size.width, stroke / 2),
                    strokeWidth = stroke
                )
            }
            .clickable(enabled = enabled, onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(12.dp)
        ) {
            Icon(
                painter = AppIcons.VenueGalleryUpload,
                contentDescription = null,
                tint = MineShaft950,
                modifier = Modifier.size(20.dp)
            )

            Text(
                text = text,
                style = LabelLarge.copy(
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 12.sp,
                    lineHeight = 16.sp
                ),
                color = MineShaft950,
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
private fun VenuePhotoTile(
    photo: VenueGalleryPhoto,
    highlightError: Boolean,
    onRemoveClick: (VenueGalleryPhoto) -> Unit,
    modifier: Modifier = Modifier
) {
    val shape = RoundedCornerShape(8.dp)
    val borderColor = if (highlightError) AccentPink else MineShaft100

    Box(
        modifier = modifier
            .clip(shape)
            .background(MineShaft50)
            .border(1.dp, borderColor, shape)
    ) {
        if (photo.imageUrl.isBlank()) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = AppIcons.Photo,
                    contentDescription = null,
                    tint = MineShaft500,
                    modifier = Modifier.size(20.dp)
                )
            }
        } else {
            AsyncImage(
                model = photo.imageUrl,
                contentDescription = "Venue photo",
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            )
        }

        Box(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(top = 16.dp, end = 16.dp)
                .size(32.dp)
                .clip(CircleShape)
                .background(MineShaft50)
                .clickable { onRemoveClick(photo) },
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = AppIcons.Close,
                contentDescription = "Remove photo",
                tint = MineShaft950,
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

private sealed interface GalleryGridItem {
    data object Upload : GalleryGridItem
    data class Photo(val value: VenueGalleryPhoto) : GalleryGridItem
}
